import { useCallback, useMemo } from 'react';
import collect from 'collect.js';
import useStore from '../context/useStore';
import { OrderMethod } from '../../types/constants';
import useBusinessHours from './useBusinessHours';
import { StoreDetails } from '../../types/store';

export default function useOrderMethodAvailability(store?: StoreDetails) {
  const { storeData: _storeData } = useStore();
  const storeData = store ?? _storeData;
  const { order_settings, has_dine_in } = storeData;

  const { isOrderMethodOpen } = useBusinessHours(storeData);

  const pickupEnabled = useMemo(
    () => order_settings.has_live_orders && order_settings.has_pickup,
    [order_settings.has_live_orders, order_settings.has_pickup],
  );

  const deliveryEnabled = useMemo(
    () => order_settings.has_live_orders && order_settings.has_delivery,
    [order_settings.has_live_orders, order_settings.has_delivery],
  );

  const onSiteEnabled = useMemo(
    () => order_settings.has_live_orders && order_settings.has_on_site,
    [order_settings.has_live_orders, order_settings.has_on_site],
  );

  const dineInEnabled = useMemo(
    () => order_settings.has_live_orders && has_dine_in,
    [order_settings.has_live_orders, has_dine_in],
  );

  const takeoutEnabled = pickupEnabled || deliveryEnabled || onSiteEnabled;

  const pickupCurrentlyAvailable = useMemo(
    () => pickupEnabled && isOrderMethodOpen(OrderMethod.Pickup),
    [pickupEnabled, isOrderMethodOpen],
  );
  const dineInCurrentlyAvailable = useMemo(
    () => dineInEnabled && isOrderMethodOpen(OrderMethod.DineIn),
    [dineInEnabled, isOrderMethodOpen],
  );

  const deliveryCurrentlyAvailable = useMemo(
    () => deliveryEnabled && isOrderMethodOpen(OrderMethod.Delivery),
    [deliveryEnabled, isOrderMethodOpen],
  );

  const onSiteCurrentlyAvailable = useMemo(
    () => onSiteEnabled && isOrderMethodOpen(OrderMethod.OnSite),
    [onSiteEnabled, isOrderMethodOpen],
  );

  const takeoutCurrentlyAvailable =
    pickupCurrentlyAvailable || deliveryCurrentlyAvailable || onSiteCurrentlyAvailable;

  const takeoutAvailableForFutureOrders = useMemo(
    () => takeoutEnabled && order_settings.has_scheduled_orders && order_settings.has_pre_order,
    [takeoutEnabled, order_settings.has_scheduled_orders, order_settings.has_pre_order],
  );

  const enabledOrderMethods = useMemo<OrderMethod[]>(
    () =>
      collect({
        [OrderMethod.Pickup]: pickupEnabled,
        [OrderMethod.Delivery]: deliveryEnabled,
        [OrderMethod.OnSite]: onSiteEnabled,
      })
        .filter((item) => Boolean(item))
        .map((enabled, method) => method)
        .values()
        .toArray(),
    [pickupEnabled, deliveryEnabled, onSiteEnabled],
  );

  const availableOrderMethods = useMemo<OrderMethod[]>(
    () =>
      collect({
        [OrderMethod.Pickup]: pickupCurrentlyAvailable,
        [OrderMethod.Delivery]: deliveryCurrentlyAvailable,
        [OrderMethod.OnSite]: onSiteCurrentlyAvailable,
        [OrderMethod.DineIn]: dineInCurrentlyAvailable,
      })
        .filter((item) => Boolean(item))
        .map((enabled, method) => method)
        .values()
        .toArray(),
    [
      pickupCurrentlyAvailable,
      deliveryCurrentlyAvailable,
      onSiteCurrentlyAvailable,
      dineInCurrentlyAvailable,
    ],
  );

  const isOrderMethodEnabled = useCallback(
    (method: OrderMethod) => enabledOrderMethods.includes(method),
    [enabledOrderMethods],
  );

  const isOrderMethodCurrentlyAvailable = useCallback(
    (method: OrderMethod) => availableOrderMethods.includes(method),
    [availableOrderMethods],
  );

  const isOrderMethodAvailableForFutureOrders = useCallback(
    (method: OrderMethod) =>
      isOrderMethodEnabled(method) &&
      order_settings.has_scheduled_orders &&
      order_settings.has_pre_order,
    [isOrderMethodEnabled, order_settings.has_scheduled_orders, order_settings.has_pre_order],
  );

  return {
    takeoutEnabled,
    pickupEnabled,
    deliveryEnabled,
    onSiteEnabled,
    takeoutCurrentlyAvailable,
    pickupCurrentlyAvailable,
    deliveryCurrentlyAvailable,
    onSiteCurrentlyAvailable,
    takeoutAvailableForFutureOrders,
    enabledOrderMethods,
    availableOrderMethods,
    isOrderMethodEnabled,
    isOrderMethodCurrentlyAvailable,
    isOrderMethodAvailableForFutureOrders,
  };
}
